import { Pic } from "@/components/site/Pic";

import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { Nav } from "@/components/site/Nav";
import { Footer } from "@/components/site/Footer";
import { TrustBand } from "@/components/site/TrustBand";
import { RelatedProducts } from "@/components/site/RelatedProducts";
import { Contact } from "@/components/site/Contact";
import { MaskLines, Reveal, SectionLabel } from "@/components/site/Motion";
import { SheetDownloads } from "@/components/site/SheetDownloads";
import { SectionCalc } from "@/components/site/SectionCalc";
import { LamellareFaq } from "@/components/site/LamellareFaq";
import { DataTable } from "@/components/site/DataTable";
import { scrollToId } from "@/lib/scroll";
import { useT, useLang } from "@/i18n";
import { usePageMeta } from "@/lib/pageMeta";
import DATA from "@/data/lamellare.json";

const STATS = [["54 – 300 mm", "xp.s0", "spessori finiti"], ["3 – 9", "xp.s1", "strati incrociati"], ["20", "xp.s2", "composizioni disponibili"], ["C24", "xp.s3", "profilo di resistenza"], ["1400 × 350 cm", "xp.s4", "dimensioni massime del pannello"], ["xp.s5v", "xp.s5", "essenza", "Abete"]];
const PHOTOS = [
  ["xlam-cantiere-pareti", "xp.ph5", "Montaggio con gru delle pareti in pannelli X-LAM EMMA INDUSTRIE in cantiere", "lg:col-span-4"],
  ["xlam-cantiere-solaio", "xp.ph6", "Posa con gru di un pannello X-LAM di copertura in cantiere", "lg:col-span-2 lg:row-span-2"],
  ["xlam-cantiere-copertura", "xp.ph7", "Casa in pannelli X-LAM EMMA INDUSTRIE con pareti e copertura montate", "lg:col-span-4"],
  ["xlam-posa", "xp.ph0", "Posa in cantiere di pareti in pannelli X-LAM EMMA INDUSTRIE", "lg:col-span-3"],
  ["p-xlam", "xp.ph1", "Pannelli X-LAM EMMA INDUSTRIE", "lg:col-span-3"],
  ["xlam-sezione-casa", "xp.ph2", "Casa in X-LAM EMMA INDUSTRIE vista in sezione", "lg:col-span-2"],
  ["xlam-parete-ventilata", "xp.ph3", "Parete X-LAM con facciata ventilata e rivestimento in legno", "lg:col-span-2"],
  ["xlam-parete-cappotto", "xp.ph4", "Parete X-LAM con cappotto isolante e intonaco", "lg:col-span-2"],
];

const Hero = ({ t }) => (
  <section className="px-5 pb-20 pt-10 sm:px-8" data-testid="xlam-hero">
    <div className="mx-auto max-w-[1500px]">
      <Link to="/" className="tap inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[#D6D0C4] transition-colors duration-300 hover:text-[#D98A52]" data-testid="xlam-page-back">
        <ArrowLeft className="h-4 w-4" /> {t("beachPage.back", "Torna alla home")}
      </Link>
      <div className="mt-14"><SectionLabel index="CLT" dark>{t("xp.label", "Pannelli a strati incrociati")}</SectionLabel></div>
      <div className="mt-10 grid gap-10 lg:grid-cols-12 lg:items-end">
        <MaskLines as="h1" onLoad className="font-display text-4xl font-bold leading-[1.02] tracking-tight sm:text-5xl lg:col-span-7 lg:text-6xl"
          lines={[t("xp.h1a", "Pannelli"), <em key="e" className="font-serif font-normal text-[#D98A52]">X-LAM.</em>]} />
        <Reveal delay={0.2} className="max-w-md lg:col-span-5">
          <p className="text-base leading-relaxed text-[#D6D0C4] sm:text-lg">{t("xp.intro", "Pareti, solai e coperture portanti in pannelli di tavole incollate a strati incrociati: 20 spessori da 54 a 300 mm, profilo di resistenza C24, prodotti e montati da EMMA INDUSTRIE.")}</p>
          <button onClick={() => scrollToId("#schede")} className="btn-amber mt-8" data-testid="xlam-hero-cta">{t("xp.cta", "Scarica la scheda X-LAM")} <ArrowUpRight className="h-4 w-4" /></button>
        </Reveal>
      </div>
      <div className="mt-16 grid grid-cols-2 gap-px bg-white/10 lg:grid-cols-3" data-testid="xlam-stats">
        {STATS.map(([v, k, l, word]) => (
          <div key={k} className="bg-[#1A1714] p-6" data-testid={`xlam-stat-${k.slice(3)}`}>
            <p className="font-display text-2xl font-bold text-[#D98A52] sm:text-3xl">{word ? t(v, word) : v}</p>
            <p className="mt-1 text-sm text-[#D6D0C4]">{t(k, l)}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Photos = ({ t }) => (
  <section className="px-5 pb-24 sm:px-8" data-testid="xlam-photos">
    <div className="mx-auto grid max-w-[1500px] grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-6">
      {PHOTOS.map(([n, k, alt, span], i) => (
        <figure key={n} className={`overflow-hidden ${span}`} data-testid={`xlam-photo-${i}`}>
          <Pic src={`/img/emma/${n}.jpg`} alt={t(k, alt)} loading="lazy" className={`h-full w-full object-cover transition-transform duration-1000 hover:scale-105 ${span.includes("row-span") ? "aspect-[4/3] lg:aspect-auto" : "aspect-[4/3]"}`} />
        </figure>
      ))}
    </div>
  </section>
);

export default function PannelliXlam() {
  const t = useT();
  const { lang } = useLang();
  const s = (DATA[lang] ?? DATA.it).sheets.xlam;
  usePageMeta({
    title: t("xp.metaTitle", "Pannelli X-LAM (CLT): spessori, strati e classe C24"),
    desc: t("xp.metaDesc", "Pannelli X-LAM EMMA INDUSTRIE da 54 a 300 mm, da 3 a 9 strati, profilo di resistenza C24. Scarica la scheda di lavoro in PDF e stima lo spessore del solaio."),
    path: "/pannelli-xlam",
    image: "/og-xlam.jpg",
    brand: "EMMA INDUSTRIE",
  });
  return (
    <div className="bg-[#1A1714] text-[#F3EFE6]" data-testid="xlam-page">
      <Nav />
      <main className="pt-36">
        <Hero t={t} />
        <Photos t={t} />
        <section className="bg-[#F7F5F0] px-5 py-24 text-[#121110] sm:px-8 lg:py-32" data-testid="xlam-specs">
          <div className="mx-auto grid max-w-[1500px] gap-16 lg:grid-cols-2">
            <Reveal className="min-w-0">
              <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{s.sub}</h2>
              <dl className="mt-6 divide-y divide-[#D8D2C6] border-y border-[#D8D2C6]" data-testid="xlam-facts">
                {(s.facts ?? []).map(([k, v]) => (
                  <div key={k} className="flex flex-wrap justify-between gap-x-6 gap-y-1 py-3">
                    <dt className="text-sm text-[#5A544C]">{k}</dt>
                    <dd className="font-semibold">{v}</dd>
                  </div>
                ))}
              </dl>
              <div className="mt-6"><DataTable cols={s.cols} rows={DATA.xlam} id="xlam-page" /></div>
              <p className="mt-3 text-sm text-[#5A544C]">{s.nominal} {s.note}</p>
            </Reveal>
            <Reveal delay={0.1} className="min-w-0">
              <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{s.propsTitle}</h2>
              <div className="mt-6"><DataTable cols={s.propsCols} rows={s.props} id="xlam-page-cl24" bold={3} /></div>
            </Reveal>
          </div>
        </section>
        <SheetDownloads keys={["xlam", "catalogo"]} />
        <SectionCalc initialMode="xlam" />
        <LamellareFaq dataKey="faqXlam" titleKey="xp.faqH" title="Domande frequenti sui pannelli X-LAM" />
        <TrustBand />
        <RelatedProducts />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}
